import { FC, useMemo } from "react";
import { UserData } from "@/entities/users/types";
import DataTable from "@/components/controls/DataTable/DataTable";
import { useTranslation } from "react-i18next";
import { DirectReportTable, TrendType } from "@/entities/reporting/types";
import { UserCell } from "@/components/controls/DataTable/ScoringTable";
import { TrendCell } from "@/components/ui/trend";

interface Props {
  data: DirectReportTable[];
  isLoading?: boolean;
  onRowClick: (row: any) => void;
}

export const DirectReportsTable: FC<Props> = ({
  data,
  isLoading,
  onRowClick
}) => {
  const { t } = useTranslation();
  const safeData = Array.isArray(data) ? data : [];

  const columns = useMemo(
    () => [
      {
        accessorKey: "user",
        header: t(`leadAnalysis.directReport.name`),
        size: 100,
        maxSize: 150,
        cell: ({ getValue }: { getValue: () => UserData }) => {
          const userData = getValue();
          return userData ? <UserCell userData={userData} /> : null;
        },
        meta: {
          textAlign: "start"
        }
      },
      {
        accessorKey: "directReportsNum",
        header: t(`leadAnalysis.directReport.directReports`),
        size: 85,
        cell: ({ getValue }: { getValue: () => number }) => {
          const value = getValue();
          return value !== undefined ? value : 0;
        },
        meta: {
          textAlign: "center"
        }
      },
      {
        id: "avgMonthlyQualifiedConvertedLeads",
        accessorKey: "avgMonthlyQualifiedConvertedLeads.trend",
        header: t(`leadAnalysis.kpi.avgNewLeads`),
        size: 85,
        cell: ({ getValue }: { getValue: () => TrendType }) => {
          const value = getValue();
          return value ? <TrendCell value={value} /> : null;
        },
        meta: {
          textAlign: "start"
        }
      },
      {
        id: "leadQualificationRate",
        accessorKey: "leadQualificationRate.trend",
        header: t(`leadAnalysis.kpi.leadQualification`),
        size: 85,
        cell: ({ getValue }: { getValue: () => TrendType }) => {
          const value = getValue();
          return value ? <TrendCell value={value} isPercentage /> : null;
        },
        meta: {
          textAlign: "start"
        }
      },
      {
        id: "leadConversionRate",
        accessorKey: "leadConversionRate.trend",
        header: t(`leadAnalysis.kpi.leadConversion`),
        size: 85,
        cell: ({ getValue }: { getValue: () => TrendType }) => {
          const value = getValue();
          return value ? <TrendCell value={value} isPercentage /> : null;
        },
        meta: {
          textAlign: "start"
        }
      }
    ],
    [t]
  );

  const handleRowClick = (row: any) => {
    onRowClick(row.original?.user);
  };

  const tableOptions = useMemo(
    () => ({
      enableRowSelection: true,
      manualPagination: true,
      enableColumnResizing: true,
      meta: {
        onClick: handleRowClick
      }
    }),
    [onRowClick]
  );

  return (
    <div className="w-full h-auto px-0 pt-0">
      <DataTable
        columns={columns}
        data={safeData}
        height="14.5rem"
        tableOptions={tableOptions}
        isDataLoading={isLoading}
        onRowClick={handleRowClick}
        headerClassName="border-none"
      />
    </div>
  );
};

export default DirectReportsTable;
